import React, { useEffect, useState } from "react";
import { Card, Select, Button, Flex, Typography, Modal, message } from "antd";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { DragIcon } from "../icon";
import { Larrow } from "../icon/Larrow";
import { API_URL_PERMISSIONS_PARTICIPATION } from "@/services/XCONS_ENV";
import { axiosInstance } from "@/utilities";
import { NIVEL } from "@/utilities/niveles-de-permiso";

const { Option } = Select;
const { Text } = Typography;

const StoreItem: React.FC<{
  participants: any;
  onChangeRole: (id: string, newRole: string) => void;
  getParticipants: any;
  type?: any;
  renderExtra?: (participant: any) => React.ReactNode;
  renderHeader?: (participant: any) => React.ReactNode;
  hideSelect?: boolean;
}> = ({ participants, onChangeRole, getParticipants, type, renderExtra, renderHeader, hideSelect }) => {
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({
      id: participants.id,
    });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    background: "#fff",
    borderRadius: "4px",
    marginBottom: "8px",
  };

  const selectOptions = [
    { value: "admin", label: NIVEL.admin },
    { value: "editor", label: NIVEL.editor },
    { value: "viewer", label: NIVEL.viewer },
    { value: "remove", label: "Remove" },
  ];

  return (
    <div ref={setNodeRef} style={style} {...attributes} className="dragCard">
      <Flex vertical style={{marginTop: 10, padding: "0 8px"}}>
        <Flex align="center" justify="space-between">
          <Flex align="center" gap={8} style={{ flex: 1 }}>
            <DragIcon {...listeners} style={{ cursor: "grab" }} />
            {renderHeader ? (
              <div style={{ flex: 1 }}>{renderHeader(participants)}</div>
            ) : (
              <Flex gap={8}>
                <Text>{participants.name}</Text>{" "}
                {participants.position === 0 && <Text type="secondary">(Dueño)</Text>}
              </Flex>
            )}
          </Flex>
          {!hideSelect && (
            <Flex gap={8} align="center">
              <Select
                className="removeOption"
                value={participants.role}
                onChange={(value) => onChangeRole(participants.id, value)}
                size="small"
                style={{ width: 120 }}
              >
                {selectOptions.map((role) => (
                  <Option key={role.value} value={role.value}>
                    {role.label}
                  </Option>
                ))}
              </Select>
            </Flex>
          )}
        </Flex>
        {renderExtra && (
          <div style={{ paddingLeft: "28px" }}>
            {renderExtra(participants)}
          </div>
        )}
      </Flex>
    </div>
  );
};

export const StoreList: React.FC<{
  participants: any[];
  title?: any;
  icon?: boolean;
  onChange: (participants: any[]) => void;
  getParticipants: any;
  type?: any;
  renderExtra?: (participant: any) => React.ReactNode;
  renderHeader?: (participant: any) => React.ReactNode;
  hideSelect?: boolean;
}> = ({
  participants,
  title = "Seller",
  icon = false,
  onChange,
  getParticipants,
  type,
  renderExtra,
  renderHeader,
  hideSelect,
}) => {
    const normalizeParticipants = (participants: any[]) => {
      const result: any[] = [];
      let nextPosition = 0;

      participants.forEach((p) => {
        const rawPos =
          typeof p.raw?.position === "number" && p.raw.position >= 0
            ? p.raw.position
            : null;

        // Si ya existe alguien con ese position, o si es null, usamos el siguiente
        const isPositionTaken = result.some((r) => r.position === rawPos);
        const finalPosition =
          rawPos !== null && !isPositionTaken ? rawPos : nextPosition++;

        result.push({
          ...p,
          position: finalPosition,
          raw: {
            ...p.raw,
            position: finalPosition,
          },
        });

        // Solo incrementamos si usamos nextPosition
        if (rawPos === null || isPositionTaken) {
          nextPosition++;
        }
      });

      return result.sort((a, b) => a.position - b.position);
    };

    const [localParticipants, setLocalParticipants] = useState<any[]>(
      normalizeParticipants(participants),
    );

    const sensors = useSensors(useSensor(PointerSensor));

    useEffect(() => {
      setLocalParticipants(normalizeParticipants(participants));
    }, [participants]);

    const handleDragEnd = (event: DragEndEvent) => {
      const { active, over } = event;
      if (over && active.id !== over.id) {
        const oldIndex = localParticipants.findIndex((s) => s.id === active.id);
        const newIndex = localParticipants.findIndex((s) => s.id === over.id);

        const newOrder = arrayMove(localParticipants, oldIndex, newIndex);

        const updatedWithPosition = newOrder.map((item, index) => ({
          ...item,
          position: index,
          raw: {
            ...item.raw,
            position: index,
          },
        }));

        setLocalParticipants(updatedWithPosition);
        onChange(updatedWithPosition);
      }
    };

    const handleRoleChange = async (id: string, newRole: string) => {
      if (newRole === "remove") {
        Modal.confirm({
          title: "¿Estas seguro que deseas eliminar esta relación?",
          okText: "Eliminar",
          cancelText: "Cancelar",
          okType: "danger",
          onOk: async () => {
            try {
              await axiosInstance.delete(
                `${API_URL_PERMISSIONS_PARTICIPATION}/${id}`,
              );
              message.success("Participante eliminado");
              getParticipants();
            } catch (error: any) {
              console.error("Error al eliminar participante", error);
              if (error.message === "You do not have permission") {
                message.error(
                  "No tienes permiso para eliminar este participante",
                );
              } else {
                message.error("No se pudo eliminar el participante");
              }
            }
          },
        });
        return;
      }

      const newParticipants = localParticipants.map((s) =>
        s.id === id ? { ...s, role: newRole } : s,
      );
      setLocalParticipants(newParticipants);
      onChange(newParticipants);
    };

    return (
      <Flex vertical={icon && type === "seller" ? false : true} style={{ width: "100%" }}>
        {icon && type === "seller" && <Larrow />}

        <Card className="drag-list-card" title={title ? <Text strong>{title}</Text> : null}>
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={localParticipants.map((s) => s.id)}
              strategy={verticalListSortingStrategy}
            >
              {localParticipants.map((participant) => (
                <StoreItem
                  key={participant.id}
                  participants={participant}
                  onChangeRole={handleRoleChange}
                  getParticipants={getParticipants}
                  renderExtra={renderExtra}
                  renderHeader={renderHeader}
                  hideSelect={hideSelect}
                />
              ))}
            </SortableContext>
          </DndContext>
        </Card>
      </Flex>
    );
  };
